/**
 * Haptic feedback helpers — wraps `navigator.vibrate` (web) and
 * `@capacitor/haptics` (native iOS/Android wrapper) behind a single
 * facade. The right backend is detected at runtime; web builds don't
 * pay any cost for the native module.
 *
 * Patterns intentionally stay short so the device never feels "buzzy."
 */

type HapticKind = "light" | "medium" | "heavy" | "tick";

const PATTERNS: Record<HapticKind, number | number[]> = {
  /** Soft tap — used for scene pad / layer toggle taps. */
  light: 10,
  /** Confirmation thump — used for record start/stop. */
  medium: 22,
  /** Firm hit — used for save / share confirmations. */
  heavy: [12, 30, 12],
  /** Quick tick — used for slider snap points (zoom landmarks, etc). */
  tick: 6,
};

// ---------------------------------------------------------------------------
// Capacitor backend (resolved lazily — the import is async so the web
// build doesn't have to bundle the native module if Capacitor isn't there).
// ---------------------------------------------------------------------------

interface CapacitorHaptics {
  impact(opts: { style: "LIGHT" | "MEDIUM" | "HEAVY" }): Promise<void>;
  selectionChanged(): Promise<void>;
}

let nativeHaptics: CapacitorHaptics | null = null;
let nativeProbed = false;

const isCapacitor = (): boolean => {
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return !!w.Capacitor?.isNativePlatform?.();
};

/**
 * Probe for the native plugin on first use. Cached in `nativeHaptics` so
 * we don't repeat the dynamic import.
 */
async function probeNative(): Promise<void> {
  if (nativeProbed) return;
  nativeProbed = true;
  if (!isCapacitor()) return;
  try {
    const mod = await import(/* @vite-ignore */ "@capacitor/haptics");
    nativeHaptics = (mod as unknown as { Haptics: CapacitorHaptics }).Haptics;
  } catch {
    /* native plugin not installed in this build — fall back to navigator.vibrate */
  }
}

// Kick the probe immediately at module load (fire-and-forget).
void probeNative();

/** Fire a haptic pulse if the device supports it. No-op on iOS Safari (web). */
export function haptic(kind: HapticKind = "light"): void {
  // Native first.
  if (nativeHaptics) {
    try {
      if (kind === "tick") {
        void nativeHaptics.selectionChanged();
      } else {
        const style = kind === "heavy" ? "HEAVY" : kind === "medium" ? "MEDIUM" : "LIGHT";
        void nativeHaptics.impact({ style });
      }
      return;
    } catch {
      /* fall through to web */
    }
  }
  // Web fallback (Android Chrome only — iOS Safari ignores this entirely).
  if (typeof navigator === "undefined") return;
  const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  if (typeof nav.vibrate !== "function") return;
  try {
    nav.vibrate(PATTERNS[kind]);
  } catch {
    /* permission denied or otherwise unavailable — silent */
  }
}
